import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2, Minimize2, EyeOff, Eye, Crown } from 'lucide-react';
import { RoomUser } from '../../message';
import { EditableRoomDisplayName } from '../EditableRoomDisplayName';
import { resolvedRoomDisplayName } from '../../roomDisplayName';

interface MembersPanelProps {
    isOpen: boolean;
    onClose: () => void;
    users: RoomUser[];
    roomDisplayName?: string;
    onRoomDisplayNameSave?: (name: string) => void;
    selectedLayout?: number | null;
    onLayoutChange?: (layout: number | null) => void;
    onFullscreen?: (userId: string) => void;
    /** Peer id of the sender whose share is in immersive fullscreen (room-wide, receiver + senders). */
    activeFullscreenPeerId?: string | null;
    onToggleHide?: (userId: string) => void;
    hiddenUserIds?: Set<string>;
}

// Get initials from name
const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

// Get avatar color based on user ID
const getAvatarColor = (userId: string): string => {
    const colors = [
        'bg-teal-500',
        'bg-pink-500',
        'bg-blue-500',
        'bg-orange-500',
        'bg-purple-500',
        'bg-green-500',
        'bg-indigo-500',
        'bg-red-500',
    ];
    
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
};

export const MembersPanel: React.FC<MembersPanelProps> = ({ 
    isOpen, 
    onClose, 
    users,
    roomDisplayName,
    onRoomDisplayNameSave,
    selectedLayout,
    onLayoutChange,
    onFullscreen,
    activeFullscreenPeerId = null,
    onToggleHide,
    hiddenUserIds = new Set()
}) => {
    const roomLabel = resolvedRoomDisplayName(roomDisplayName);
    const nameEditable = Boolean(onRoomDisplayNameSave);
    const layoutOptions = [1, 2, 4, 8, 12, 16];

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, x: 20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.95, x: 20 }}
                    transition={{ duration: 0.2 }}
                    className="fixed right-8 bottom-20 w-96 z-50"
                    style={{ maxHeight: 'calc(100vh - 120px)' }}
                >
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden h-full">
                        {/* Header */}
                        <div className="flex items-center justify-between p-5 border-b border-gray-200">
                            <div>
                                <EditableRoomDisplayName
                                    value={roomLabel}
                                    onSave={(name) => onRoomDisplayNameSave?.(name)}
                                    editable={nameEditable}
                                    className="text-lg font-semibold text-gray-800 block"
                                    inputClassName="w-full max-w-[240px] border border-gray-300 rounded-lg px-2 py-1 text-lg font-semibold text-gray-900"
                                    as="div"
                                />
                                <h2 className="text-sm font-medium text-gray-500 mt-1">
                                    Participants ({users.length})
                                </h2>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Members List */}
                        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-0">
                            {users.map((user) => (
                                <div
                                    key={user.id}
                                    className="flex items-center justify-between py-3 hover:bg-gray-50 rounded-lg px-2 transition-colors"
                                >
                                    {/* Left: Fullscreen (senders) + Avatar + Info */}
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        {user.role === 'sender' &&
                                            (user.streaming || activeFullscreenPeerId === user.id) &&
                                            onFullscreen && (
                                                <button
                                                    type="button"
                                                    onClick={() => onFullscreen(user.id)}
                                                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
                                                    title={
                                                        activeFullscreenPeerId === user.id
                                                            ? 'Exit fullscreen tile'
                                                            : 'Fullscreen this share'
                                                    }
                                                >
                                                    {activeFullscreenPeerId === user.id ? (
                                                        <Minimize2 className="w-4 h-4 text-gray-600" />
                                                    ) : (
                                                        <Maximize2 className="w-4 h-4 text-gray-600" />
                                                    )}
                                                </button>
                                            )}
                                        {/* Circular Avatar */}
                                        <div className={`w-12 h-12 rounded-full ${getAvatarColor(user.id)} flex items-center justify-center flex-shrink-0`}>
                                            <span className="text-white font-semibold text-base">
                                                {getInitials(user.name)}
                                            </span>
                                        </div>
                                        
                                        {/* Name + Device + Role */}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <div className="font-semibold text-gray-900 text-base truncate">
                                                    {user.name}
                                                </div>
                                                {user.you && (
                                                    <Crown className="w-4 h-4 text-yellow-500 flex-shrink-0" fill="currentColor" />
                                                )}
                                            </div>
                                            <div className="text-sm text-gray-500 flex items-center gap-2">
                                                <span>Desktop</span>
                                                {user.role && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="capitalize">
                                                            {user.role === 'sender' && user.streaming
                                                                ? 'streaming'
                                                                : user.role}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Right: hide — fullscreen control is left of the name */}
                                    {user.role === 'sender' && (
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => onToggleHide?.(user.id)}
                                                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                                title={hiddenUserIds.has(user.id) ? "Show participant" : "Hide participant"}
                                            >
                                                {hiddenUserIds.has(user.id) ? (
                                                    <EyeOff className="w-4 h-4 text-gray-600" />
                                                ) : (
                                                    <Eye className="w-4 h-4 text-gray-600" />
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Layout Grid Section */}
                        <div className="border-t border-gray-200 p-5 flex-shrink-0">
                            <div className="text-base font-medium text-gray-500 mb-3">Layout</div>
                            <div className="grid grid-cols-6 gap-2">
                                {layoutOptions.map(count => (
                                    <button
                                        key={count}
                                        onClick={() => onLayoutChange?.(selectedLayout === count ? null : count)}
                                        className={`
                                            h-12 rounded-xl font-semibold text-base transition-all
                                            ${selectedLayout === count 
                                                ? 'bg-purple-100 text-purple-600 border-2 border-purple-500' 
                                                : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                                            }
                                        `}
                                    >
                                        {count}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    </motion.div>
            )}
        </AnimatePresence>
    );
};
